import React from 'react';
import { AlertTriangle } from 'lucide-react';
import i18n from '../i18n';
import { EmptyState } from '../design-system/components/EmptyState';
import { Button } from '../design-system/components/Button';

interface State { error: Error | null }

/** Contains a crash to the page that threw. Without it, one module throwing
 *  during render unmounted the whole app — sidebar included — and left a
 *  blank window. AppShell keys it by page, so navigating away recovers. */
export class PageErrorBoundary extends React.Component<{ children: React.ReactNode }, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    // Keep it in the console (and the client error buffer that issue reports attach).
    console.error('Page crashed:', error, info.componentStack);
  }

  render() {
    if (!this.state.error) return this.props.children;
    const t = i18n.t.bind(i18n);
    return (
      <EmptyState
        icon={<AlertTriangle size={20} />}
        title={t('shell.pageCrashed', 'This page hit an error')}
        description={<>
          {t('shell.pageCrashedHint', 'The rest of the app still works. Try again, or report it with the details below.')}
          <br />
          <code style={{ fontFamily: 'var(--font-mono)', fontSize: 11 }}>{this.state.error.message}</code>
        </>}
        action={<>
          <Button onClick={() => this.setState({ error: null })}>{t('shell.tryAgain', 'Try again')}</Button>{' '}
          <Button variant="ghost" onClick={() => window.dispatchEvent(new CustomEvent('nexus:report-issue'))}>
            {t('nav.reportIssue', 'Report an issue')}
          </Button>
        </>}
      />
    );
  }
}

export default PageErrorBoundary;
